import * as React from 'react'
import Link from 'next/link'
import getConfig from 'next/config'
import { useTranslation } from 'next-i18next'

import {
  Box,
  Button,
  ButtonVariant,
  Flex,
  Form,
  Icon,
  StyledError,
  TextField,
  Theme,
  Typography,
} from '@web-apps/ui-shared'

import { ErrorType, LINK_SERVICES } from '../../types'
import { StyledText, StyledWrapper } from './GetStartedPageForm.styles'

type FormDataType = { [LINK_SERVICES.instagram]: string }

type Props = {
  initialValues: FormDataType
  registrationLink: string
  isLoading: boolean
  handleSubmit: (d: FormDataType) => void
  handleAnalytics: (d: string) => void
  error?: ErrorType
  partner?: string
}

export const GetStartedPageForm = ({
  initialValues,
  handleSubmit,
  isLoading,
  handleAnalytics,
  error,
  registrationLink,
  partner,
}: Props) => {
  const { t } = useTranslation(['pages', 'common'])
  const { publicRuntimeConfig } = getConfig()

  return (
    <StyledWrapper
      homePage={publicRuntimeConfig.baseUrls.commercialWebsite}
      partner={partner}
    >
      <Box mt={82}>
        <Box my={16}>
          <Box mb={8}>
            <Typography variant="h2" as="h2">
              <Flex gap={6} justify="center">
                <span>{t('pages:get_started.title')}</span>
                <Icon.MagicWand width={26} height={26} />
                <Icon.Sparkles width={20} height={26} />
              </Flex>
            </Typography>
          </Box>
          <StyledText>{t(`pages:get_started.description`)}</StyledText>
        </Box>
        <Form
          onSubmit={handleSubmit}
          initialValues={initialValues}
          validations={{
            descriptors: {
              [LINK_SERVICES.instagram]: {
                required: t('common:errors.cant_be_blank'),
              },
            },
          }}
        >
          {() => (
            <>
              <Box mb={16}>
                <TextField
                  name={LINK_SERVICES.instagram}
                  placeholder={t('pages:get_started.placeholder')}
                  leftDecorator={<Icon.InstagramMono />}
                  errorMessages={
                    error?.serviceType === LINK_SERVICES.instagram
                      ? [error.message]
                      : undefined
                  }
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                />
              </Box>
              <Flex direction="column" gap={16}>
                <Button variant={ButtonVariant.PRIMARY} isLoading={isLoading}>
                  <Flex justify="center" gap={16}>
                    <span>{t('pages:get_started.continue_button')}</span>
                    <Icon.ArrowRight
                      width={16}
                      height={16}
                      fillColor={Theme.Colors.typography.inverse}
                    />
                  </Flex>
                </Button>
                <Link href={registrationLink} passHref>
                  <Button
                    inverse
                    outline={false}
                    clickHandler={() => handleAnalytics('skip_button_click')}
                  >
                    {t('pages:get_started.skip_button')}
                  </Button>
                </Link>
                {error && !error.serviceType && (
                  <Flex justify="center">
                    <StyledError>{error.message}</StyledError>
                  </Flex>
                )}
              </Flex>
            </>
          )}
        </Form>
      </Box>
    </StyledWrapper>
  )
}
